import { Exercise as ComponentExercise } from "@/services/admin/exercises.interfaces";

/**
 * Função utilitária para analisar dados de exercício quando eles estão em formato JSON string
 * @param exercise O exercício a ser processado
 * @returns O exercício com o campo data analisado como objeto, se aplicável
 */
export function parseExerciseData<T extends ComponentExercise>(exercise: T): T {
  if (!exercise) return exercise;

  try {
    // Se data é uma string, tenta fazer parse como JSON
    if (exercise.data && typeof exercise.data === 'string') {
      return {
        ...exercise,
        data: JSON.parse(exercise.data)
      };
    }
  } catch (error) {
    console.error('Erro ao processar dados JSON do exercício:', error);
  }

  // Retorna o exercício original se não houve necessidade de parsing ou se houve erro
  return exercise;
}

/**
 * Função para extrair o campo específico de um objeto que pode estar aninhado
 * Útil para acessar valores de exercícios em diferentes formatos
 */
export function getExerciseProperty<T>(
  exercise: any, 
  property: string, 
  defaultValue: T
): T {
  if (!exercise) return defaultValue;
  
  try {
    // Tenta acessar o campo diretamente no exercício
    if (property in exercise && exercise[property] !== undefined) {
      return exercise[property];
    }
    
    // Tenta acessar o campo dentro de data se for um objeto
    if (exercise.data && typeof exercise.data === 'object' && property in exercise.data) {
      return exercise.data[property];
    }
    
    // Tenta fazer parse do campo data se for uma string
    if (exercise.data && typeof exercise.data === 'string') {
      try {
        const parsedData = JSON.parse(exercise.data);
        if (parsedData && property in parsedData) {
          return parsedData[property];
        }
      } catch (e) {
        console.error('Erro ao fazer parse de dados do exercício:', e);
      }
    }
  } catch (error) {
    console.error(`Erro ao acessar propriedade ${property} do exercício:`, error);
  }
  
  return defaultValue;
}

/**
 * Uma versão simplificada da função acima, especificamente para verificar
 * se o exercício tem uma determinada propriedade
 */
export function hasExerciseProperty(exercise: any, property: string): boolean {
  return getExerciseProperty(exercise, property, undefined) !== undefined;
} 